"use client";

import React, { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-default font-semibold text-foreground relative py-2 after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-0 after:-translate-x-1/2 after:bg-primary after:transition-all after:duration-300 hover:after:w-full hover:text-primary transition-colors tracking-tight">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground">
          <Menu className="size-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background">
        <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
        <div className="flex flex-col gap-8 mt-8">
          <Link to="/HomePage" onClick={() => setIsOpen(false)}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_post_logo.png" alt="The Peachtree Post" className="h-7" />
          </Link>
          {loading ? <div className="flex flex-col gap-4">
              {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-full bg-muted animate-pulse rounded-sm" />)}
            </div> : <nav className="flex flex-col gap-6">
              {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="font-default font-semibold text-lg text-foreground hover:text-primary transition-colors tracking-tight" onClick={() => setIsOpen(false)}>
                  {item.label}
                </Link>)}
              <Button asChild className="rounded-sm bg-primary text-primary-foreground w-full mt-4 shadow-none hover:bg-primary/90 transition-transform hover:scale-[1.02]">
                <Link to="/ContactPage" onClick={() => setIsOpen(false)}>Get Started</Link>
              </Button>
            </nav>}
        </div>
      </SheetContent>
    </Sheet>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="234083" id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm" : "bg-background border-b border-border/50"}`}>
      <header className="container mx-auto px-4 h-20 flex items-center justify-between">
        <div className="flex items-center">
          <Link to="/HomePage">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_post_logo.png" alt="The Peachtree Post" className="h-7" />
          </Link>
        </div>

        <div className="flex items-center gap-8">
          <MenuProvider menu_id="16">
            <DesktopNav />
          </MenuProvider>

          <Button asChild className="hidden md:inline-flex rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 shadow-none transition-transform hover:scale-[1.02] font-default font-bold">
            <Link to="/ContactPage">Get Started</Link>
          </Button>

          <MenuProvider menu_id="16">
            <MobileNav />
          </MenuProvider>
        </div>
      </header>
    </section>;
}